// Editor for `epx:MachineLearningDecision` boxed expressions.
//
// Mirrors the `renderXxx(expr, ctx)` contract used by the seven standard
// boxed-expression renderers in views.js so the dispatcher in
// BoxedExpressionEditor._dispatch can invoke it the same way. Layout:
//
//   1. Modelo    — modelRef / modelVersion / runtime / endpoint
//   2. Entradas  — table of (feature, source FEEL, typeRef)
//   3. Salidas   — table of (field, typeRef)
//   4. Fallback  — radios value/rule/error with conditional input
//
// All mutations target the moddle tree directly. ctx.onChange is invoked so
// the editor refreshes and the host can mark the document dirty.
//
// The third arg (decision) is optional — when present it powers the
// "Suggest bindings from Information Requirements" button by reading
// `informationRequirement` and the linked target's variable name.

import { makeEditable } from './shared';
import { makeFeelEditable } from './feelField';
import { FEEL_TYPES } from '../widgets/feelTypes';
import { sanitizeFeelIdent } from '../feel/ident';

const RUNTIMES = ['rest', 'pmml', 'onnx'];
const FALLBACK_STRATEGIES = ['value', 'rule', 'error'];

function uid(prefix) {
  return prefix + '_' + Math.random().toString(36).slice(2, 9);
}

function snakeCase(str) {
  if (!str) return '';
  return String(str)
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/([a-z\d])([A-Z])/g, '$1_$2')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();
}

function buildTypeRefSelect(currentType, onPick) {
  const sel = document.createElement('select');
  sel.className = 'epx-dmn-expr__ml-typeselect';
  FEEL_TYPES.forEach((t) => {
    const opt = document.createElement('option');
    opt.value = t;
    opt.textContent = t;
    if ((currentType || 'string') === t) opt.selected = true;
    sel.appendChild(opt);
  });
  sel.addEventListener('change', () => onPick(sel.value || null));
  return sel;
}

function buildSection(title) {
  const wrap = document.createElement('section');
  wrap.className = 'epx-dmn-expr__ml-section';
  const h = document.createElement('h4');
  h.className = 'epx-dmn-expr__ml-section-title';
  h.textContent = title;
  wrap.appendChild(h);
  return wrap;
}

function buildField(label, control) {
  const row = document.createElement('label');
  row.className = 'epx-dmn-expr__ml-field';
  const lab = document.createElement('span');
  lab.className = 'epx-dmn-expr__ml-field-label';
  lab.textContent = label;
  row.appendChild(lab);
  row.appendChild(control);
  return row;
}

function plainInput(value, placeholder, commit) {
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'epx-dmn-expr__ml-input';
  input.value = value == null ? '' : String(value);
  input.placeholder = placeholder || '';
  input.addEventListener('change', () => commit(input.value));
  input.addEventListener('blur', () => commit(input.value));
  return input;
}

function numberInput(value, placeholder, commit) {
  const input = document.createElement('input');
  input.type = 'number';
  input.step = '0.01';
  input.min = '0';
  input.max = '1';
  input.className = 'epx-dmn-expr__ml-input epx-dmn-expr__ml-input--number';
  input.value = value == null || value === '' ? '' : String(value);
  input.placeholder = placeholder || '';
  input.addEventListener('change', () => {
    const raw = input.value;
    if (raw === '' || raw == null) return commit(null);
    const n = Number(raw);
    commit(Number.isFinite(n) ? n : null);
  });
  return input;
}

// ──────────────────────────────────────────────────────────────────────────
// Section renderers
// ──────────────────────────────────────────────────────────────────────────

function renderModelSection(expr, ctx) {
  const sec = buildSection('Modelo');
  sec.appendChild(buildField('modelRef', plainInput(expr.modelRef, 'e.g. risk-classifier',
    (v) => { expr.modelRef = v || ''; ctx.onChange(); })));
  sec.appendChild(buildField('modelVersion', plainInput(expr.modelVersion, 'e.g. v1.3.0',
    (v) => { expr.modelVersion = v || ''; ctx.onChange(); })));

  const runtimeSel = document.createElement('select');
  runtimeSel.className = 'epx-dmn-expr__ml-typeselect';
  RUNTIMES.forEach((r) => {
    const opt = document.createElement('option');
    opt.value = r;
    opt.textContent = r;
    if ((expr.runtime || 'rest') === r) opt.selected = true;
    runtimeSel.appendChild(opt);
  });
  runtimeSel.addEventListener('change', () => {
    expr.runtime = runtimeSel.value;
    ctx.onChange();
  });
  sec.appendChild(buildField('runtime', runtimeSel));

  sec.appendChild(buildField('endpoint', plainInput(expr.endpoint, 'https://…',
    (v) => { expr.endpoint = v || ''; ctx.onChange(); })));

  sec.appendChild(buildField('confidenceThreshold', numberInput(
    expr.confidenceThreshold,
    '0.0 – 1.0',
    (v) => { expr.confidenceThreshold = v; ctx.onChange(); },
  )));

  return sec;
}

function renderInputBindingsSection(expr, ctx, decision) {
  const sec = buildSection('Entradas');
  if (!expr.inputBindings) expr.inputBindings = [];

  const table = document.createElement('table');
  table.className = 'epx-dmn-expr__grid epx-dmn-expr__ml-grid';
  const thead = document.createElement('thead');
  const headRow = document.createElement('tr');
  ['feature', 'source (FEEL)', 'typeRef', ''].forEach((label) => {
    const th = document.createElement('th');
    th.textContent = label;
    headRow.appendChild(th);
  });
  thead.appendChild(headRow);
  table.appendChild(thead);

  const tbody = document.createElement('tbody');
  expr.inputBindings.forEach((b, idx) => {
    const tr = document.createElement('tr');

    const featTd = document.createElement('td');
    featTd.appendChild(makeEditable(b.feature || '', 'feature_name', (v) => {
      b.feature = v;
      ctx.onChange();
    }));
    tr.appendChild(featTd);

    const srcTd = document.createElement('td');
    srcTd.className = 'epx-dmn-expr__cell';
    srcTd.appendChild(makeFeelEditable(
      b.source || '',
      'FEEL expression',
      (v) => { b.source = v; ctx.onChange(); },
      ctx.getScope,
    ));
    tr.appendChild(srcTd);

    const typeTd = document.createElement('td');
    typeTd.appendChild(buildTypeRefSelect(b.typeRef, (t) => {
      b.typeRef = t;
      ctx.onChange();
    }));
    tr.appendChild(typeTd);

    const rmTd = document.createElement('td');
    rmTd.className = 'epx-dmn-expr__type';
    const rm = document.createElement('button');
    rm.type = 'button';
    rm.className = 'epx-dmn-expr__row-remove';
    rm.title = 'Remove input binding';
    rm.textContent = '×';
    rm.addEventListener('click', () => {
      expr.inputBindings.splice(idx, 1);
      ctx.onChange();
    });
    rmTd.appendChild(rm);
    tr.appendChild(rmTd);

    tbody.appendChild(tr);
  });

  const addTr = document.createElement('tr');
  const addTd = document.createElement('td');
  addTd.colSpan = 4;
  addTd.className = 'epx-dmn-expr__add-row';
  addTd.textContent = '+ Add input binding';
  addTd.addEventListener('click', () => {
    expr.inputBindings.push(ctx.moddle.create('epx:InputBinding', {
      feature: '', source: '', typeRef: 'string',
    }));
    ctx.onChange();
  });
  addTr.appendChild(addTd);
  tbody.appendChild(addTr);
  table.appendChild(tbody);
  sec.appendChild(table);

  // Suggest bindings from Information Requirements — for each requirement on
  // the owning decision, look up the upstream element through the FEEL scope
  // (computeScope already resolves variable.name / typeRef for every visible
  // input or upstream decision) and create one inputBinding per name. The
  // feature is snake_case-d from the variable name, the source is the FEEL
  // identifier itself. Skips features already present so repeated clicks
  // don't create duplicates.
  if (decision) {
    const tools = document.createElement('div');
    tools.className = 'epx-dmn-expr__ml-tools';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'epx-dmn-expr__ml-suggest';
    btn.textContent = 'Sugerir bindings desde Information Requirements';
    btn.addEventListener('click', () => {
      const scope = typeof ctx.getScope === 'function' ? ctx.getScope() : [];
      // Only pull from items the requirements actually expose: input data
      // and upstream decisions. computeScope marks them with kind === 'input'
      // / 'decision' (sourceKind 'InputData' / 'Decision'). Drop 'self', BKM
      // function refs and parameters — they don't represent ML features.
      const allowKinds = new Set(['input', 'decision']);
      const candidates = scope.filter((s) => allowKinds.has(s.kind));
      const existing = new Set((expr.inputBindings || []).map((b) => b.feature).filter(Boolean));
      let added = 0;
      candidates.forEach((entry) => {
        const variableName = entry.name;
        if (!variableName) return;
        const feature = snakeCase(variableName);
        if (!feature || existing.has(feature)) return;
        const source = sanitizeFeelIdent(variableName) || variableName;
        expr.inputBindings.push(ctx.moddle.create('epx:InputBinding', {
          feature, source, typeRef: entry.typeRef || 'string',
        }));
        existing.add(feature);
        added += 1;
      });
      if (added > 0) ctx.onChange();
    });
    tools.appendChild(btn);
    sec.appendChild(tools);
  }

  return sec;
}

function renderOutputBindingsSection(expr, ctx) {
  const sec = buildSection('Salidas');
  if (!expr.outputBindings) expr.outputBindings = [];

  const table = document.createElement('table');
  table.className = 'epx-dmn-expr__grid epx-dmn-expr__ml-grid';
  const thead = document.createElement('thead');
  const headRow = document.createElement('tr');
  ['field', 'typeRef', ''].forEach((label) => {
    const th = document.createElement('th');
    th.textContent = label;
    headRow.appendChild(th);
  });
  thead.appendChild(headRow);
  table.appendChild(thead);

  const tbody = document.createElement('tbody');
  expr.outputBindings.forEach((b, idx) => {
    const tr = document.createElement('tr');
    const fieldTd = document.createElement('td');
    fieldTd.appendChild(makeEditable(b.field || '', 'field_name', (v) => {
      b.field = v;
      ctx.onChange();
    }));
    tr.appendChild(fieldTd);

    const typeTd = document.createElement('td');
    typeTd.appendChild(buildTypeRefSelect(b.typeRef, (t) => {
      b.typeRef = t;
      ctx.onChange();
    }));
    tr.appendChild(typeTd);

    const rmTd = document.createElement('td');
    rmTd.className = 'epx-dmn-expr__type';
    const rm = document.createElement('button');
    rm.type = 'button';
    rm.className = 'epx-dmn-expr__row-remove';
    rm.title = 'Remove output binding';
    rm.textContent = '×';
    rm.addEventListener('click', () => {
      expr.outputBindings.splice(idx, 1);
      ctx.onChange();
    });
    rmTd.appendChild(rm);
    tr.appendChild(rmTd);
    tbody.appendChild(tr);
  });

  const addTr = document.createElement('tr');
  const addTd = document.createElement('td');
  addTd.colSpan = 3;
  addTd.className = 'epx-dmn-expr__add-row';
  addTd.textContent = '+ Add output binding';
  addTd.addEventListener('click', () => {
    expr.outputBindings.push(ctx.moddle.create('epx:OutputBinding', {
      field: '', typeRef: 'string',
    }));
    ctx.onChange();
  });
  addTr.appendChild(addTd);
  tbody.appendChild(addTr);
  table.appendChild(tbody);
  sec.appendChild(table);
  return sec;
}

function renderFallbackSection(expr, ctx) {
  const sec = buildSection('Fallback');
  if (!expr.fallback) {
    // Lazy-create only when the user picks a strategy — track local state so
    // un-selecting reverts to "no fallback configured".
  }

  const radios = document.createElement('div');
  radios.className = 'epx-dmn-expr__ml-radios';
  const groupName = 'epx-ml-fallback-' + uid('strat');
  const currentStrategy = (expr.fallback && expr.fallback.strategy) || '';
  FALLBACK_STRATEGIES.forEach((strat) => {
    const lbl = document.createElement('label');
    lbl.className = 'epx-dmn-expr__ml-radio';
    const r = document.createElement('input');
    r.type = 'radio';
    r.name = groupName;
    r.value = strat;
    if (strat === currentStrategy) r.checked = true;
    r.addEventListener('change', () => {
      if (!r.checked) return;
      if (!expr.fallback) {
        expr.fallback = ctx.moddle.create('epx:Fallback', { strategy: strat, value: '' });
      } else {
        expr.fallback.strategy = strat;
      }
      ctx.onChange();
    });
    lbl.appendChild(r);
    const span = document.createElement('span');
    span.textContent = strat;
    lbl.appendChild(span);
    radios.appendChild(lbl);
  });
  sec.appendChild(radios);

  // The conditional value field. Only shown for strategy === 'value' (FEEL
  // expression evaluated against the current scope) or 'rule' (free-form
  // identifier of a sibling Decision/BKM).
  if (currentStrategy === 'value') {
    const fieldWrap = document.createElement('div');
    fieldWrap.className = 'epx-dmn-expr__ml-fallback-value';
    fieldWrap.appendChild(makeFeelEditable(
      (expr.fallback && expr.fallback.value) || '',
      'FEEL expression',
      (v) => { expr.fallback.value = v; ctx.onChange(); },
      ctx.getScope,
    ));
    sec.appendChild(fieldWrap);
  } else if (currentStrategy === 'rule') {
    const fieldWrap = document.createElement('div');
    fieldWrap.className = 'epx-dmn-expr__ml-fallback-value';
    fieldWrap.appendChild(plainInput(
      (expr.fallback && expr.fallback.value) || '',
      'Decision or BKM name',
      (v) => { expr.fallback.value = v || ''; ctx.onChange(); },
    ));
    sec.appendChild(fieldWrap);
  }
  return sec;
}

// ──────────────────────────────────────────────────────────────────────────
// Public renderer — same signature as the renderXxx exports in views.js so
// BoxedExpressionEditor._dispatch can call it through the shared `ctx.render`
// recursion contract.
// ──────────────────────────────────────────────────────────────────────────
export function renderMlDecision(expr, ctx, decision = null) {
  const box = document.createElement('div');
  box.className = 'epx-dmn-expr__box epx-dmn-expr__box--ml';

  // Defensive defaults so a freshly-created (or partially-deserialized) ML
  // decision renders without crashing the editor.
  if (!expr.runtime) expr.runtime = 'rest';
  if (!expr.inputBindings) expr.inputBindings = [];
  if (!expr.outputBindings) expr.outputBindings = [];

  box.appendChild(renderModelSection(expr, ctx));
  box.appendChild(renderInputBindingsSection(expr, ctx, decision));
  box.appendChild(renderOutputBindingsSection(expr, ctx));
  box.appendChild(renderFallbackSection(expr, ctx));

  return box;
}
